import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { quotesApi } from '@/services/quotes'
import { invalidateNotificationQueries } from '@/hooks/useNotifications'
import { invalidatePortalMetricQueries } from '@/hooks/usePortalMetrics'
import { INTERVENTION_TYPE, JOB_STATUS } from '@/types'
import type {
  ApiResponse,
  CreateOptionalFeeTemplateRequest,
  JobDetail,
  OptionalFeeTemplate,
  Quote,
  QuoteListQuery,
  CreateQuoteRequest,
  EditQuoteRequest,
  ListQuery,
  SendDocumentRequest,
  ServiceItemRequest,
} from '@/types'

type QueryInvalidator = Pick<
  ReturnType<typeof useQueryClient>,
  'invalidateQueries'
>

export function invalidateQuoteQueries(qc: QueryInvalidator, id: string) {
  qc.invalidateQueries({ queryKey: ['quotes', 'list'] })
  qc.invalidateQueries({ queryKey: ['quotes', 'detail', id] })
  qc.invalidateQueries({ queryKey: ['quotes', 'history', id] })
  qc.invalidateQueries({ queryKey: ['quotes', 'summary'] })
  invalidatePortalMetricQueries(qc)
  invalidateNotificationQueries(qc)
}

function syncLinkedJobQuoteCache(
  qc: ReturnType<typeof useQueryClient>,
  quote: Quote | undefined | null,
  startLinkedVisitQuoteJob = false
) {
  if (!quote?.job_id) return

  qc.setQueryData<ApiResponse<JobDetail>>(
    ['jobs', 'detail', quote.job_id],
    current => {
      if (!current?.data) return current

      const linkedQuote = {
        id: quote.id,
        number: quote.number ?? null,
        status: quote.status,
        total: quote.total ?? 0,
        issue_date: quote.issue_date ?? null,
      }
      const existingQuotes = current.data.quotes ?? []
      const nextQuotes = [
        linkedQuote,
        ...existingQuotes.filter(item => item.id !== quote.id),
      ]

      return {
        ...current,
        data: {
          ...current.data,
          status:
            startLinkedVisitQuoteJob &&
            current.data.intervention_type === INTERVENTION_TYPE.visit_quote &&
            current.data.status !== JOB_STATUS.in_progress &&
            current.data.status !== JOB_STATUS.completed &&
            current.data.status !== JOB_STATUS.cancelled
              ? JOB_STATUS.in_progress
              : current.data.status,
          started_at:
            startLinkedVisitQuoteJob &&
            current.data.intervention_type === INTERVENTION_TYPE.visit_quote
              ? (current.data.started_at ?? new Date().toISOString())
              : current.data.started_at,
          quotes: nextQuotes,
          quote_reference:
            current.data.quote_reference?.id === quote.id ||
            current.data.intervention_type === INTERVENTION_TYPE.visit_quote
              ? linkedQuote
              : current.data.quote_reference,
        },
      }
    }
  )

  qc.invalidateQueries({ queryKey: ['jobs', 'detail', quote.job_id] })
  qc.invalidateQueries({ queryKey: ['agenda'] })
}

export function useQuotesList(q: QuoteListQuery) {
  return useQuery({
    queryKey: ['quotes', 'list', q],
    queryFn: () => quotesApi.list(q),
  })
}

// Tenant-wide summary card counts. KPI clicks filter the list only; the summary
// stays stable so card values do not collapse to the selected bucket.
export function useQuotesSummary() {
  return useQuery({
    queryKey: ['quotes', 'summary'],
    queryFn: () => quotesApi.summary(),
  })
}

export function useQuote(id: string | undefined) {
  return useQuery({
    queryKey: ['quotes', 'detail', id],
    queryFn: () => quotesApi.get(id as string),
    enabled: !!id,
  })
}

export function useQuoteHistory(id: string | undefined, q: ListQuery) {
  return useQuery({
    queryKey: ['quotes', 'history', id, q],
    queryFn: () => quotesApi.history(id as string, q),
    enabled: !!id,
  })
}

export function useCreateQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateQuoteRequest) => quotesApi.create(body),
    onSuccess: response => {
      qc.invalidateQueries({ queryKey: ['quotes', 'list'] })
      qc.invalidateQueries({ queryKey: ['quotes', 'summary'] })
      invalidatePortalMetricQueries(qc)
      invalidateNotificationQueries(qc)
      syncLinkedJobQuoteCache(qc, response.data, true)
    },
  })
}

export function useQuoteOptionalFeeTemplates() {
  return useQuery({
    queryKey: ['quotes', 'optional-fee-templates'],
    queryFn: () => quotesApi.listOptionalFeeTemplates(),
  })
}

export function useCreateQuoteOptionalFeeTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateOptionalFeeTemplateRequest) =>
      quotesApi.createOptionalFeeTemplate(body),
    onSuccess: response => {
      const createdTemplate = response.data

      if (createdTemplate?.is_active) {
        qc.setQueryData<ApiResponse<OptionalFeeTemplate[]>>(
          ['quotes', 'optional-fee-templates'],
          current => {
            const currentData = current?.data ?? []
            return {
              success: current?.success ?? response.success,
              message: current?.message ?? response.message,
              detail: current?.detail ?? response.detail,
              data: [
                createdTemplate,
                ...currentData.filter(item => item.id !== createdTemplate.id),
              ],
              errors: current?.errors,
              metadata: current?.metadata,
            }
          }
        )
      }

      qc.invalidateQueries({ queryKey: ['quotes', 'optional-fee-templates'] })
      qc.invalidateQueries({ queryKey: ['settings', 'optional-fees'] })
    },
  })
}

export function useCreateQuoteServiceItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ServiceItemRequest) => quotesApi.createServiceItem(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'service-items'] })
      qc.invalidateQueries({ queryKey: ['settings', 'service-categories'] })
    },
  })
}

export function useUpdateQuote(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EditQuoteRequest) => quotesApi.update(id, body),
    onSuccess: response => {
      invalidateQuoteQueries(qc, id)
      syncLinkedJobQuoteCache(qc, response.data)
    },
  })
}

export function useSendQuote(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SendDocumentRequest) => quotesApi.send(id, body),
    onSuccess: response => {
      invalidateQuoteQueries(qc, id)
      syncLinkedJobQuoteCache(qc, response.data)
    },
  })
}

export function useDeleteQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => quotesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quotes', 'list'] })
      invalidatePortalMetricQueries(qc)
      invalidateNotificationQueries(qc)
    },
  })
}
